"use client";
import { apiGet } from "@/utils/apiClient";
import { usePathname } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";
import SectionHeading from "./SectionHeading";
import Icon from "@/components/icons/Icons";
import { useLoginModal } from "@/utils/useLoginModal";
import Image from "next/image";
import { AnimatePresence, motion } from "framer-motion";

export type HoldingRow = {
  market_cap: number | null;
  instrument: string | null;
  stocks: string | null; // Small Cap / Mid Cap / Large Cap
  industry: string | null;
  holdings: number | null;
  value: number | null;
  sector_pe_ratio: number | null;
  pe_ratio: number | null;
  return_1y: number | null;
};

type ApiResponse = {
  data: HoldingRow[];
  success: boolean;
};

type APIValue = {
  slug: string;
  id: number | null;
};

export default function StocksTable({ slug, id }: APIValue) {
  const pathname = usePathname();
  const headers = [
    "Stocks",
    "Market Cap",
    "Sectors",
    "Holdings",
    "PE Ratio",
    "1Yr Returns",
    "Values",
  ];

  const { isLoggedIn, open, setModalKey } = useLoginModal();
  const sectionRef = useRef<HTMLDivElement | null>(null);

  const [stocks, setStocks] = useState<HoldingRow[]>([]);
  const [visibleCount, setVisibleCount] = useState(10);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: keyof HoldingRow | null;
    direction: "asc" | "desc";
  }>({
    key: null,
    direction: "desc",
  });

  const fetchDataStocks = async () => {
    setLoading(true);
    try {
      const res: ApiResponse = await apiGet(
        `/api/stocks?slug=${slug}&id=${id}`,
        { base: "none" },
      );
      if (!res.success) throw new Error("Something went wrong");

      const filteredStocks = res.data.filter((stock) => stock.stocks);

      setStocks(filteredStocks);
    } catch (error) {
      console.error("Failed to fetch stocks data:", error);
    } finally {
      setLoading(false);
    }
  };

  const normalizedStocks = stocks.map((stock) => ({
    ...stock,
    value: stock.value ? Number(stock.market_cap) : 0,
  }));

  useEffect(() => {
    if (!pathname.includes("portfolio-details")) return;

    const timer = setTimeout(() => {
      fetchDataStocks();
    }, 500);

    return () => clearTimeout(timer);
  }, [slug, id, pathname]);

  const filteredStocks = isLoggedIn
    ? normalizedStocks.filter((stock) =>
        stock.instrument?.toLowerCase().includes(search.trim().toLowerCase()),
      )
    : normalizedStocks;

  const isAllVisible = visibleCount >= filteredStocks.length;

  const sortedStocks = React.useMemo(() => {
    if (!sortConfig.key) return filteredStocks;

    return [...filteredStocks].sort((a, b) => {
      const valA = a[sortConfig.key!];
      const valB = b[sortConfig.key!];

      if (valA == null) return 1;
      if (valB == null) return -1;

      if (typeof valA === "number" && typeof valB === "number") {
        return sortConfig.direction === "asc" ? valA - valB : valB - valA;
      }

      return sortConfig.direction === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
  }, [filteredStocks, sortConfig]);

  const handleSort = (key: keyof HoldingRow) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        // toggle asc/desc
        return { key, direction: prev.direction === "desc" ? "asc" : "desc" };
      }
      // new sort always starts as desc
      return { key, direction: "desc" };
    });
  };

  const handleToggle = () => {
    if (visibleCount >= filteredStocks.length) {
      if (sectionRef.current) {
        const targetTop =
          sectionRef.current.getBoundingClientRect().top + window.scrollY - 200;

        window.scrollTo({
          top: targetTop,
          behavior: "smooth",
        });

        const handleScroll = () => {
          const currentScroll = window.scrollY;

          if (Math.abs(currentScroll - targetTop) < 2) {
            setVisibleCount(10);
            window.removeEventListener("scroll", handleScroll);
          }
        };

        window.addEventListener("scroll", handleScroll);
      }
    } else {
      setVisibleCount((prev) => Math.min(prev + 10, filteredStocks.length));
    }
  };

  if (!pathname.includes("portfolio-details") || !stocks) return;

  const dummyRows: HoldingRow[] = Array.from({ length: 7 }).map((_, idx) => ({
    instrument:
      idx % 2 === 0 ? "Login required" : "Login required for more data",
    stocks: "----",
    industry: "----",
    holdings: 0,
    sector_pe_ratio: null,
    pe_ratio: null,
    return_1y: null,
    value: 0,
    market_cap: 0,
  }));

  if (normalizedStocks.length <= 0) return;

  return (
    <>
      <div
        ref={sectionRef}
        className="flex flex-wrap justify-start items-center mb-4 gap-3"
      >
        <SectionHeading heading="Current Stocks Holdings of Equity" />

        <div className="relative w-full sm:w-72 ml-auto group">
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setVisibleCount(10);
            }}
            disabled={loading || !isLoggedIn}
            className={`w-full pl-4 pr-10 py-2 rounded-full border border-[#8A8A8E33] text-2sm leading-5 transition duration-350 ${loading || !isLoggedIn ? "opacity-50 cursor-not-allowed" : "group-hover:border-[#b0cfff] "}`}
          />
          {!search ? (
            <Icon.searchIcon className="absolute right-5 w-5 h-5 top-1/2 -translate-y-1/2 text-[#8A8A8E] transition duration-350 group-hover:text-primaryBlue" />
          ) : (
            <Icon.popupCloseIcon
              onClick={() => setSearch("")}
              className="absolute cursor-pointer right-5 w-4 h-4 top-1/2 -translate-y-1/2 text-[#8A8A8E] transition duration-350  hover:text-primaryBlue rotate-45"
            />
          )}
        </div>
      </div>

      <div
        className={`relative rounded-4xl bg-white p-5 sm:p-6 scrollbar-xs ${loading || (!isLoggedIn && stocks.length >= 3) ? "overflow-hidden" : "overflow-x-auto"} text-sm text-primaryBlack border border-[#E9E9EB]`}
      >
        {loading && (
          <div className="absolute left-0 right-0 top-0 bottom-0 inset-0 z-2 bg-black/10 backdrop-blur-xs flex items-center justify-center">
            <span className="transform w-16 h-16 border-4 border-solid border-transparent border-t-[#0c3e9f] rounded-full animate-spin" />
          </div>
        )}

        <table className="w-full">
          <thead>
            <tr className="text-[#444444] font-semibold">
              {headers.map((header, index) => {
                const keyMap: (keyof HoldingRow)[] = [
                  "instrument",
                  "stocks",
                  "industry",
                  "holdings",
                  "pe_ratio",
                  "return_1y",
                  "value",
                ];

                const key = keyMap[index];

                return (
                  <th
                    key={index}
                    onClick={() => handleSort(key)}
                    className="text-center whitespace-nowrap first:text-left py-2.5 px-3 sm:py-3 sm:px-4 border-b border-[#E9E9EB] cursor-pointer select-none"
                  >
                    <div className="flex items-center justify-center gap-1">
                      {header}
                      <div className="flex flex-col gap-px ml-0.5">
                        <i
                          className={`inline-block border-l-[3px] border-l-transparent border-r-[3px] border-r-transparent border-b-4 ${sortConfig.key === key && sortConfig.direction === "desc" ? "border-b-primaryBlack" : "border-b-[#6b6b6b]"}`}
                        />

                        <i
                          className={`inline-block border-l-[3px] border-l-transparent border-r-[3px] border-r-transparent border-t-4 ${sortConfig.key === key && sortConfig.direction === "asc" ? "border-t-primaryBlack" : "border-t-[#6b6b6b]"}`}
                        />
                      </div>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            <AnimatePresence initial={false}>
              {isLoggedIn ? (
                filteredStocks.length > 0 ? (
                  sortedStocks.slice(0, visibleCount).map((stock, idx) => (
                    <motion.tr
                      key={idx}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="border-b last:border-none border-[#E9E9EB]"
                    >
                      <td
                        className="text-left py-2.5 px-3 sm:py-3 sm:px-4 max-w-28 truncate"
                        title={stock.instrument || "Stock"}
                      >
                        {stock.instrument || "-"}
                      </td>
                      <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4">
                        {stock.stocks || "-"}
                      </td>
                      <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4">
                        {stock.industry || "-"}{" "}
                        <span className="text-xs text-[#464646]">
                          ({stock.sector_pe_ratio || "-"})
                        </span>
                      </td>
                      <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4">
                        {stock.holdings
                          ? `${Number(stock.holdings).toFixed(2)}%`
                          : "-"}
                      </td>

                      <td
                        className={`text-center py-2.5 px-3 sm:py-3 sm:px-4 text-gray-500`}
                      >
                        {stock.pe_ratio !== null
                          ? `${Number(stock.pe_ratio).toFixed(2)}`
                          : "-"}
                      </td>

                      <td
                        className={`text-center py-2.5 px-3 sm:py-3 sm:px-4 
                      ${stock.return_1y !== null ? (stock.return_1y < 0 ? "text-red-500" : "text-green-600") : "text-gray-500"}`}
                      >
                        {stock.return_1y !== null
                          ? `${Number(stock.return_1y).toFixed(2)}%`
                          : "-"}
                      </td>
                      <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4">
                        {stock.value
                          ? `${Number(stock.value).toFixed(2)}Cr`
                          : "-"}
                      </td>
                    </motion.tr>
                  ))
                ) : (
                  <>
                    <tr>
                      <td colSpan={7} className="py-8 text-center">
                        {!loading && (
                          <>
                            <Image
                              src="/next_assets/images/no-data-found.png"
                              alt="No results found"
                              width={200}
                              height={200}
                              className="mx-auto mb-4"
                            />
                            <p className="text-gray-500 text-center">
                              No matching records found
                            </p>
                          </>
                        )}
                      </td>
                    </tr>
                  </>
                )
              ) : (
                <>
                  {stocks.length >= 3 ? (
                    <>
                      {normalizedStocks.slice(0, 3).map((stock, idx) => (
                        <tr
                          key={idx}
                          className="border-b last:border-none border-[#E9E9EB]"
                        >
                          <td className="text-left py-2.5 px-3 sm:py-3 sm:px-4 max-w-28 truncate">
                            {stock.instrument || "-"}
                          </td>
                          <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4">
                            {stock.stocks || "-"}
                          </td>
                          <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4">
                            {stock.industry || "-"}{" "}
                            <span className="text-xs text-[#464646]">
                              ({stock.sector_pe_ratio || "-"})
                            </span>
                          </td>
                          <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4">
                            {stock.holdings
                              ? `${Number(stock.holdings).toFixed(2)}%`
                              : "-"}
                          </td>
                          <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4">
                            {stock.pe_ratio
                              ? Number(stock.pe_ratio).toFixed(2)
                              : "-"}
                          </td>
                          <td
                            className={`text-center py-2.5 px-3 sm:py-3 sm:px-4 
                      ${stock.return_1y !== null ? (stock.return_1y < 0 ? "text-red-500" : "text-green-600") : "text-gray-500"}`}
                          >
                            {stock.return_1y !== null
                              ? `${Number(stock.return_1y).toFixed(2)}%`
                              : "-"}
                          </td>
                          <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4">
                            {stock.value
                              ? `${Number(stock.value).toFixed(2)}Cr`
                              : "-"}
                          </td>
                        </tr>
                      ))}

                      {dummyRows.map((dummy, idx) => (
                        <tr
                          key={`dummy-${idx}`}
                          className="border-b border-[#E9E9EB] opacity-60"
                        >
                          <td className="text-left py-2.5 px-3 sm:py-3 sm:px-4 max-w-28 truncate">
                            {dummy.instrument}
                          </td>
                          <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4">
                            {dummy.stocks}
                          </td>
                          <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4">
                            {dummy.industry || "-"}{" "}
                            <span className="text-xs text-[#464646]">
                              ({dummy.sector_pe_ratio || "-"})
                            </span>
                          </td>
                          <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4">
                            00%
                          </td>
                          <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4">
                            00
                          </td>
                          <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4 text-green-600">
                            00%
                          </td>
                          <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4">
                            00Cr
                          </td>
                        </tr>
                      ))}
                    </>
                  ) : (
                    normalizedStocks.map((stock, idx) => (
                      <tr
                        key={idx}
                        className="border-b last:border-none border-[#E9E9EB]"
                      >
                        <td className="text-left py-2.5 px-3 sm:py-3 sm:px-4 max-w-28 truncate">
                          {stock.instrument || "-"}
                        </td>
                        <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4">
                          {stock.stocks || "-"}
                        </td>
                        <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4">
                          {stock.industry || "-"}{" "}
                          <span className="text-xs text-[#464646]">
                            ({stock.sector_pe_ratio || "-"})
                          </span>
                        </td>
                        <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4">
                          {stock.holdings
                            ? `${Number(stock.holdings).toFixed(2)}%`
                            : "-"}
                        </td>
                        <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4">
                          {stock.pe_ratio
                            ? Number(stock.pe_ratio).toFixed(2)
                            : "-"}
                        </td>
                        <td
                          className={`text-center py-2.5 px-3 sm:py-3 sm:px-4 
                      ${stock.return_1y !== null ? (stock.return_1y < 0 ? "text-red-500" : "text-green-600") : "text-gray-500"}`}
                        >
                          {stock.return_1y !== null
                            ? `${Number(stock.return_1y).toFixed(2)}%`
                            : "-"}
                        </td>
                        <td className="text-center py-2.5 px-3 sm:py-3 sm:px-4">
                          {stock.value
                            ? `${Number(stock.value).toFixed(2)}Cr`
                            : "-"}
                        </td>
                      </tr>
                    ))
                  )}
                </>
              )}
            </AnimatePresence>
          </tbody>
        </table>

        {!isLoggedIn && stocks.length >= 3 && (
          <div className="absolute inset-0 z-1 bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,rgba(255,255,255,0.39)_41.78%)] flex items-center justify-center text-center text-sm sm:text-base font-medium text-primaryBlack px-4">
            <div className="gradient-blur">
              <div /> <div /> <div /> <div /> <div /> <div />
            </div>

            <div className="m-auto w-fit h-fit relative z-1">
              <Image
                src={"/next_assets/images/scheme-details/lock-data.webp"}
                alt="Login to unlock Rolling and Discrete Returns"
                width={133}
                height={133}
                className="object-contain mx-auto shrink-0"
              />
              <button
                onClick={() => {
                  open();
                  setModalKey("stock-holding-table");
                }}
                className="group flex items-center gap-4 px-7 py-3 text-base leading-5 border border-primaryBlue text-primaryBlue rounded-xl bg-white hover:bg-primaryBlue hover:text-white transition duration-300 mt-5"
              >
                Unlock Now
                <Icon.dropdownDownArrow className="w-3 h-3 -rotate-90 text-primaryBlue group-hover:text-white transition duration-300" />
              </button>
            </div>
          </div>
        )}
      </div>

      {isLoggedIn && filteredStocks.length > 10 && (
        <div className="flex justify-center mt-4">
          <button
            onClick={handleToggle}
            disabled={loading}
            className={`px-4 py-2 rounded-xl transition ${loading ? "bg-gray-300 text-gray-500 cursor-not-allowed" : isAllVisible ? "bg-white text-primaryBlue hover:bg-primaryBlue/10 border border-primaryBlue" : "bg-primaryBlue text-white hover:bg-primaryBlue/80"}`}
          >
            {loading ? "Loading..." : isAllVisible ? "Show Less" : "Show More"}
          </button>
        </div>
      )}
    </>
  );
}
